import { useEffect, useMemo, useState } from 'react';
import { createRoute, useNavigate, useSearch } from '@tanstack/react-router';
import { useQuery } from '@tanstack/react-query';
import {
  capacidadeDaMaquina,
  CHAVES_DE_CAPACIDADE,
  combinarModelos,
  dentroDeAlgumaRaiz,
  MODELO_PADRAO,
  modeloPadrao,
  MODO_PADRAO,
  MODOS_PERMISSAO,
  modoDisponivelNaMaquina,
  modosDoMotor,
  modoPerigoso,
  ROTAS,
  type IdModoPermissao,
  type ModeloOpencode,
  type RaizPermitida,
} from '@remoto/shared';
import {
  MOTORES,
  ROTULO_DO_MOTOR,
  sugerirMotor,
  type CotaClaude,
  type CotaMotor,
  type Motor,
} from '@remoto/protocolo';
import { rotaPainel } from './painel';
import { chamar } from '../api';
import { usarSessao } from '../estado/sessao';
import {Cartao} from '../componentes/layout';
import { CaixaDeMarcar, CampoDeEscolha } from '../componentes/campos';
import { SeloForaDeCasa } from '../componentes/SeloForaDeCasa';
import { EscolhaDeModelo } from '../configuracoes/EscolhaDeModelo';
import { provedorDoCatalogo, usarCatalogo } from '../configuracoes/dados';
import { esforcoAposTrocarModelo } from '../configuracoes/regras';
import { Tela } from '../workspace/Tela';
import type { RecursosDoServidor } from '../workspace/paginas';
import { mensagemDoErro } from '../erros';
import {
  catalogoDoMotor,
  esforcoAoTrocarDeMotor,
  fraseDaFaltaDeChave,
  lerCatalogoOpencode,
  motivoOpencodeIndisponivel,
  motorFechado,
  motorSaiDeCasa,
  opcoesDeEsforco,
  origemDoCatalogo,
  recursoOpencodeLigado,
  textoDaConfirmacaoOpencode,
  textoDaCota,
  type MaquinaParaOpencode,
} from '../sessoes/criacao-por-motor';

interface MaquinaDisponivel extends MaquinaParaOpencode {
  id: string;
  nome: string;
  estado: string;
  papel: string;
  raizesPermitidas: RaizPermitida[];
  capacidadeSdk: number;
  sessoesAtivas: number;
  /** O relatório da sonda: é dele que sai se esta máquina fala Codex e OpenCode. */
  capacidades?: unknown;
  /**
   * O bypass total (ADR-0023) valendo AQUI, resolvido pela API (a chave dela e
   * a do daemon). Ausente é API anterior: o bypass do Codex na VPS não aparece.
   */
  bypassTotal?: boolean;
  /**
   * O retrato do Codex daquela máquina, com o CATÁLOGO de modelos.
   *
   * A tela de criar precisa da lista antes de existir sessão, então
   * `sdk.capacidades` (que é por sessão) não serve aqui. Sem catálogo a tela
   * desabilita criar e diz por quê — nunca oferece um nome que ninguém
   * confirmou existir.
   */
  codex?: { ok?: boolean; erro?: string | null; modelos?: ModeloDaMaquina[] } | null;
  cotaClaude?: CotaClaude | null;
  cotaCodex?: CotaMotor | null;
}

/** O que `combinarModelos` consome — a forma que a máquina reporta. */
interface ModeloDaMaquina {
  id: string;
  rotulo?: string;
  padrao?: boolean;
  esforcoPadrao?: string | null;
  esforcosDoMotor?: string[];
}

function NovaSessao(): React.JSX.Element {
  const navegar = useNavigate();
  // `?cwd=` vem do "continuar nesta pasta" de uma sessão observada: quem clicou
  // ali já disse em que pasta quer trabalhar, e pedir de novo seria burocracia.
  const busca = useSearch({ strict: false }) as { cwd?: string };
  const [maquinaId, setMaquinaId] = useState('');
  const [cwd, setCwd] = useState(busca.cwd ?? '');
  const [motor, setMotor] = useState<Motor>('CLAUDE');
  const [modo, setModo] = useState<string>(MODO_PADRAO);
  const [modelo, setModelo] = useState<string>(MODELO_PADRAO);
  const [esforco, setEsforco] = useState('high');
  const [confirmacaoFable, setConfirmacaoFable] = useState(false);
  const [confirmacaoModoPerigoso, setConfirmacaoModoPerigoso] = useState(false);
  const [confirmacaoOpencode, setConfirmacaoOpencode] = useState(false);
  /**
   * O modelo do OpenCode que a pessoa ESCOLHEU, inteiro. A busca vai ao
   * servidor e o achado pode não estar na lista que a tela carregou ao abrir;
   * sem guardá-lo, os esforços dele (que são POR MODELO) sumiriam.
   */
  const [escolhidoNoCatalogo, setEscolhidoNoCatalogo] = useState<ModeloOpencode | null>(null);
  const papel = usarSessao((e) => e.usuario?.papel);
  const [primeiraMensagem, setPrimeiraMensagem] = useState('');
  const [erro, setErro] = useState('');
  const [criando, setCriando] = useState(false);

  const { data: maquinas = [] } = useQuery({
    queryKey: ['maquinas'],
    queryFn: () => chamar<MaquinaDisponivel[]>(ROTAS.maquinas),
  });

  const noAr = maquinas.filter((m) => m.estado === 'ONLINE');

  /**
   * A máquina que o formulário já sabe qual é.
   *
   * Com a pasta vinda de fora, é a que tem essa pasta liberada — não faz
   * sentido perguntar quando só uma pode servir. E quando existe uma máquina
   * no ar e só uma, o "escolha…" é um clique com uma resposta só: o campo
   * nasce preenchido (com a primeira raiz dela na pasta), e continua trocável.
   */
  useEffect(() => {
    if (maquinaId) return;
    const dona = cwd ? noAr.find((m) => dentroDeAlgumaRaiz(cwd, m.raizesPermitidas)) : undefined;
    const alvo = dona ?? (!cwd && noAr.length === 1 ? noAr[0] : undefined);
    if (!alvo) return;
    setMaquinaId(alvo.id);
    if (!cwd) setCwd(alvo.raizesPermitidas[0]?.caminho ?? '');
  }, [cwd, maquinaId, noAr]);
  const escolhida = noAr.find((m) => m.id === maquinaId);

  /*
    O catálogo do OpenCode é da FROTA (a API lê do provedor ativo), não da máquina
    — e a lista SEM busca é lida mesmo antes de a pessoa escolher o motor,
    porque é o 404 dela, junto com o `recursos.opencode` do `/auth/eu`, que diz
    que o recurso está desligado neste painel (D32). Sem isso a opção
    apareceria aberta e a criação esbarraria depois de tudo preenchido. A
    busca em si mora no `EscolhaDeModelo`, a mesma da tela de Configurações.
  */
  const { data: eu } = useQuery({
    queryKey: ['eu'],
    queryFn: () => chamar<{ recursos?: RecursosDoServidor }>(ROTAS.auth.eu),
    staleTime: 5 * 60_000,
  });
  const consultaOpencode = usarCatalogo('', noAr.length > 0);
  const catalogoOpencode = lerCatalogoOpencode(consultaOpencode.data, consultaOpencode.error);
  const recursoLigado = recursoOpencodeLigado(eu?.recursos, catalogoOpencode);
  /*
    O provedor ATIVO (F2), lido do mesmo catálogo: é ele que o selo, a
    confirmação e a linha das cotas nomeiam. `null` enquanto o catálogo não
    chega — e aí os textos não nomeiam ninguém, em vez de chutar.
  */
  const provedorAtivo = provedorDoCatalogo(consultaOpencode.data);

  /** Os modelos do OpenCode que a tela conhece: os da lista e o escolhido. */
  const loteDoCatalogo = consultaOpencode.data;
  const conhecidos = useMemo(() => {
    const mapa = new Map<string, ModeloOpencode>();
    for (const m of loteDoCatalogo?.modelos ?? []) mapa.set(m.id, m);
    if (escolhidoNoCatalogo) mapa.set(escolhidoNoCatalogo.id, escolhidoNoCatalogo);
    return mapa;
  }, [loteDoCatalogo, escolhidoNoCatalogo]);

  /*
    O catálogo vem de um lugar diferente em cada motor — da casa, da máquina ou
    do provedor de modelos da frota —, e `combinarModelos` é quem sabe a diferença. O motor é
    parâmetro obrigatório lá: sem ele, o ramo errado devolveria o catálogo do
    outro CLI sem erro nenhum, e a tela ofereceria `opus` ao app-server da
    OpenAI.
  */
  const fontes = {
    codex: escolhida?.codex?.modelos ?? [],
    opencode: [...conhecidos.values()],
    padraoOpencode: loteDoCatalogo?.padrao?.modelo ?? null,
  };
  const modelosOferecidos = combinarModelos(catalogoDoMotor(motor, fontes), motor);
  const origem = origemDoCatalogo(motor);
  const semCatalogo = origem === 'da-maquina' && modelosOferecidos.length === 0;
  const escolhidoAgora = modelosOferecidos.find((m) => m.id === modelo);
  const exigeConfirmar = escolhidoAgora?.exigeConfirmacao ?? false;
  const perigosoEscolhido = modoPerigoso(modo as IdModoPermissao);
  const esforcos = opcoesDeEsforco(motor, escolhidoAgora);
  const saiDeCasa = motorSaiDeCasa(motor);

  /*
    A capacidade é da MÁQUINA e do MOTOR: uma máquina sem Codex configurado
    não some da lista — ela aparece com a opção desabilitada e o motivo que a
    sonda escreveu, porque "sumiu" não ensina nada a quem precisa ligar a
    chave.
  */
  const doCodex = capacidadeDaMaquina(escolhida?.capacidades, CHAVES_DE_CAPACIDADE.codex);
  const motivoOpencode = motivoOpencodeIndisponivel(recursoLigado, escolhida, provedorAtivo);
  const fechadoAgora = motorFechado(motor, { codex: doCodex, motivoOpencode });

  /*
    A sugestão NÃO escolhe: o roteamento é manual, por decisão do dono. Ela é
    a frase ao lado das cotas — e vem sempre com o motivo junto, porque
    sugestão sem porquê vira ordem, e ordem que a pessoa não entende ela
    aprende a ignorar. O OpenCode só entra nela se a tela deixa escolhê-lo:
    sugerir um motor desabilitado é pior que não sugerir (D21).
  */
  const sugestao = sugerirMotor({
    claude: escolhida?.cotaClaude ?? null,
    codex: escolhida?.cotaCodex ?? null,
    agora: new Date(),
    opencodeDisponivel: motivoOpencode === null,
  });

  /** Troca de motor troca o que faz sentido junto: modo, modelo e esforço. */
  function escolherMotor(novo: Motor): void {
    setMotor(novo);
    setConfirmacaoFable(false);
    setConfirmacaoOpencode(false);
    setEscolhidoNoCatalogo(null);
    const catalogo = catalogoDoMotor(novo, fontes);
    setModelo(modeloPadrao(novo, catalogo) ?? '');
    if (!modosDoMotor(novo).includes(modo as IdModoPermissao)) setModo(MODO_PADRAO);
    const padrao = combinarModelos(catalogo, novo).find((m) => m.padrao);
    setEsforco(esforcoAoTrocarDeMotor(novo, padrao, esforco));
  }

  async function criar(): Promise<void> {
    setCriando(true);
    setErro('');
    try {
      const criada = await chamar<{ id: string }>(ROTAS.sessoes, {
        method: 'POST',
        corpo: {
          maquinaId,
          motor,
          cwd: cwd.trim(),
          modo,
          modelo,
          esforco,
          confirmacaoFable,
          confirmacaoModoPerigoso,
          confirmacaoOpencode,
          ...(primeiraMensagem.trim() ? { primeiraMensagem: primeiraMensagem.trim() } : {}),
        },
      });
      void navegar({ to: '/sessoes/$id', params: { id: criada.id } });
    } catch (e) {
      setErro(
        mensagemDoErro(e, 'Não foi possível iniciar a sessão.', {
          sem_permissao: 'Modos que dispensam aprovação exigem papel ADMIN.',
          // Pelo código, com o NOME do provedor ativo (F2): a tela de Configurações tem duas chaves.
          opencode_sem_chave: fraseDaFaltaDeChave(provedorAtivo),
        }),
      );
    } finally {
      setCriando(false);
    }
  }

  return (
    <Tela>
      <Cartao titulo="Nova sessão">
        {noAr.length === 0 ? (
          <p className="text-sm text-zinc-500">
            Nenhuma máquina no ar. Ligue o agente numa delas para poder iniciar uma sessão.
          </p>
        ) : (
          <div className="space-y-4">
            <CampoDeEscolha
              rotulo="Máquina"
              valor={maquinaId || null}
              opcoes={noAr.map((m) => ({
                id: m.id,
                rotulo: m.nome,
                descricao: `${m.sessoesAtivas} de ${m.capacidadeSdk} sessões em uso`,
              }))}
              aoEscolher={(id) => {
                setMaquinaId(id);
                const alvo = noAr.find((m) => m.id === id);
                setCwd(alvo?.raizesPermitidas[0]?.caminho ?? '');
              }}
            />

            <label className="block space-y-1.5">
              <span className="text-sm text-zinc-400">Pasta</span>
              <input
                value={cwd}
                onChange={(e) => setCwd(e.target.value)}
                placeholder="caminho completo do projeto"
                className="w-full rounded-md border border-zinc-700 bg-zinc-950 px-3 py-2 font-mono text-sm text-zinc-100 outline-none focus:border-zinc-500"
              />
              {escolhida ? (
                <span className="block text-xs text-zinc-500">
                  liberadas: {escolhida.raizesPermitidas.map((r) => r.caminho).join(' · ') || 'nenhuma'}
                </span>
              ) : null}
            </label>

            <div className="space-y-2">
              <CampoDeEscolha<Motor>
                rotulo="Motor"
                valor={motor}
                opcoes={MOTORES.map((id) => {
                  /*
                    A opção NÃO some quando falta capacidade: ela aparece
                    desabilitada, com o motivo. Sumir não ensina nada a quem
                    precisa ligar a chave — e "desligado", "binário não
                    encontrado", "não logado" e "falta a chave da frota" pedem
                    gestos diferentes, de pessoas diferentes.
                  */
                  const fechado = motorFechado(id, { codex: doCodex, motivoOpencode });
                  return {
                    id,
                    rotulo: ROTULO_DO_MOTOR[id],
                    ...(fechado ? { desabilitada: true, ...fechado } : {}),
                  };
                })}
                aoEscolher={escolherMotor}
              />
              {saiDeCasa ? <SeloForaDeCasa provedor={provedorAtivo} /> : null}
              {fechadoAgora ? (
                // A máquina mudou depois da escolha (ou a chave caiu): o motor
                // escolhido fechou, e a tela diz por quê em vez de só apagar o
                // botão de criar.
                <p
                  className="rounded-md border border-amber-900 bg-amber-950/30 px-3 py-2 text-sm text-amber-200"
                  data-teste="motor-fechado"
                >
                  {ROTULO_DO_MOTOR[motor]} indisponível aqui: {fechadoAgora.motivo}
                </p>
              ) : null}
            </div>

            {/*
              As cotas à vista, com a sugestão ao lado. O roteamento é manual:
              isto informa, nunca escolhe.
            */}
            <p className="text-xs text-zinc-500" data-teste="cotas-dos-motores">
              {MOTORES.map(
                (id) =>
                  `${ROTULO_DO_MOTOR[id]}: ${textoDaCota(
                    id,
                    { claude: escolhida?.cotaClaude ?? null, codex: escolhida?.cotaCodex ?? null },
                    new Date(),
                    provedorAtivo,
                  )}`,
              ).join(' · ')}
            </p>
            <p className="text-xs text-zinc-500" data-teste="sugestao-de-motor">
              {sugestao.motivo}
            </p>

            {semCatalogo ? (
              <p
                className="rounded-md border border-amber-900 bg-amber-950/30 px-3 py-2 text-sm text-amber-200"
                data-teste="sem-catalogo"
              >
                Esta máquina ainda não reportou os modelos do {ROTULO_DO_MOTOR[motor]}. Sem a lista
                não dá para escolher um — e inventar um nome faria a sessão nascer quebrada. Tente
                de novo em um minuto, ou confira o motor na tela de Saúde.
              </p>
            ) : null}

            {origem === 'do-provedor' && catalogoOpencode.estado === 'falhou' ? (
              /*
                Sem catálogo não se sabe nem QUAL provedor está ativo: a frase
                fala no catálogo de modelos, sem nomear ninguém.
              */
              <p className="text-xs text-amber-300" data-teste="catalogo-opencode-falhou">
                Não deu para ler o catálogo de modelos do OpenCode agora. O modelo padrão da frota
                continua valendo; tente a busca do campo Modelo de novo em instantes.
              </p>
            ) : null}

            <div className="grid gap-3 sm:grid-cols-2">
              <CampoDeEscolha<IdModoPermissao>
                rotulo="Como pedir permissão"
                valor={modo as IdModoPermissao}
                opcoes={MODOS_PERMISSAO.filter(
                  (m) =>
                    /*
                      Cada motor tem os modos dele: `plan` não existe no Codex
                      (o motor não tem o gesto que destrava), `auto` e
                      `dontAsk` não existem no OpenCode (D15). A API recusa com
                      código próprio — oferecer e recusar depois seria ensinar
                      um caminho que não leva a lugar nenhum.

                      E na VPS o Codex perde `bypassPermissions`: lá o sandbox
                      é a única barreira do motor, e esse modo é justamente o
                      que o desliga.
                    */
                    modosDoMotor(motor).includes(m.id as IdModoPermissao) &&
                    modoDisponivelNaMaquina(
                      m.id as IdModoPermissao,
                      motor,
                      escolhida?.papel === 'VPS' ? 'VPS' : 'PC',
                      escolhida?.bypassTotal === true,
                    ),
                ).map((m) => ({
                  id: m.id,
                  rotulo: m.rotulo,
                  // Âmbar aqui é só cor: quem confirma o risco é a caixa de
                  // marcar que aparece logo abaixo.
                  perigoso: m.perigoso,
                  // O gate de verdade fica na API; aqui é só a porta — e ela
                  // diz por que está fechada, em vez de esconder a opção.
                  ...(m.perigoso && papel !== 'ADMIN'
                    ? {
                        desabilitada: true,
                        descricao: 'só ADMIN',
                        motivo: 'modos que dispensam aprovação exigem papel ADMIN',
                      }
                    : {}),
                }))}
                aoEscolher={(id) => {
                  setModo(id);
                  setConfirmacaoModoPerigoso(false);
                }}
              />

              {origem === 'do-provedor' ? (
                /*
                  O catálogo do provedor tem centenas de modelos (D12): o
                  campo é o da tela de Configurações, com a busca do `Seletor`
                  indo ao servidor e os favoritos primeiro, com preço, contexto
                  e "aceita imagem". E o esforço sobrevive à troca de modelo só
                  quando o novo o tem — senão volta ao padrão do provedor.
                */
                <EscolhaDeModelo
                  rotulo="Modelo"
                  valor={modelo || null}
                  conhecidos={conhecidos}
                  aoEscolher={(escolhido) => {
                    setModelo(escolhido.id);
                    setEscolhidoNoCatalogo(escolhido);
                    setEsforco(esforcoAposTrocarModelo(esforco, escolhido));
                  }}
                />
              ) : (
                <CampoDeEscolha
                  rotulo="Modelo"
                  valor={modelo || null}
                  opcoes={modelosOferecidos.map((m) => ({
                    id: m.id,
                    // O rótulo da máquina, ou o id CRU. Id feio é melhor que
                    // nome inventado.
                    rotulo: m.rotulo,
                    perigoso: m.exigeConfirmacao,
                  }))}
                  aoEscolher={(id) => {
                    setModelo(id);
                    setConfirmacaoFable(false);
                    const alvo = modelosOferecidos.find((m) => m.id === id);
                    if (alvo?.esforcoPadrao) setEsforco(alvo.esforcoPadrao);
                  }}
                />
              )}

              {esforcos.length > 1 ? (
                <CampoDeEscolha
                  rotulo="Esforço de raciocínio"
                  valor={esforco}
                  /*
                    Os esforços do MODELO quando ele os reporta (o Codex e o
                    OpenCode têm os deles, fora do enum do Claude); a lista da
                    casa quando não. Oferecer `xhigh` a um modelo que só aceita
                    três seria pedir o que o motor recusa.
                  */
                  opcoes={esforcos}
                  aoEscolher={setEsforco}
                />
              ) : (
                <p className="self-end text-xs text-zinc-500" data-teste="sem-esforco">
                  Este modelo não tem níveis de esforço: vale o padrão do provedor.
                </p>
              )}
            </div>

            {perigosoEscolhido && papel === 'ADMIN' ? (
              <CaixaDeMarcar
                marcada={confirmacaoModoPerigoso}
                aoMarcar={setConfirmacaoModoPerigoso}
                className="rounded-md border border-amber-900 bg-amber-950/30 px-3 py-2 text-sm text-amber-200"
              >
                Entendo que neste modo a sessão age sem pedir aprovação, e assumo o risco.
              </CaixaDeMarcar>
            ) : null}

            {exigeConfirmar ? (
              <CaixaDeMarcar
                marcada={confirmacaoFable}
                aoMarcar={setConfirmacaoFable}
                className="rounded-md border border-amber-900 bg-amber-950/30 px-3 py-2 text-sm text-amber-200"
              >
                Entendo que este modelo cobra crédito extra sem perguntar quando a cota acaba.
              </CaixaDeMarcar>
            ) : null}

            {saiDeCasa ? (
              <CaixaDeMarcar
                marcada={confirmacaoOpencode}
                aoMarcar={setConfirmacaoOpencode}
                className="rounded-md border border-amber-900 bg-amber-950/30 px-3 py-2 text-sm text-amber-200"
              >
                {textoDaConfirmacaoOpencode(provedorAtivo)}
              </CaixaDeMarcar>
            ) : null}

            <label className="block space-y-1.5">
              <span className="text-sm text-zinc-400">Primeira mensagem (opcional)</span>
              <textarea
                value={primeiraMensagem}
                onChange={(e) => setPrimeiraMensagem(e.target.value)}
                rows={3}
                className="w-full rounded-md border border-zinc-700 bg-zinc-950 px-3 py-2 text-zinc-100 outline-none focus:border-zinc-500"
              />
            </label>

            {erro ? <p className="text-sm text-red-300">{erro}</p> : null}

            <button
              onClick={() => void criar()}
              disabled={
                !maquinaId ||
                cwd.trim().length === 0 ||
                criando ||
                // Sem modelo não se cria: a regra da casa manda modelo
                // explícito em toda sessão, e no Codex ele só existe se a
                // máquina reportou o catálogo.
                modelo.trim().length === 0 ||
                fechadoAgora !== null ||
                // A confirmação do OpenCode (D10) é da pessoa, não da tela: a
                // API recusa sem ela, e o botão não finge que dá.
                (saiDeCasa && !confirmacaoOpencode)
              }
              className="w-full rounded-md bg-zinc-100 px-4 py-2.5 font-medium text-zinc-900 transition hover:bg-white disabled:opacity-50"
            >
              {criando ? 'iniciando…' : 'Iniciar sessão'}
            </button>
          </div>
        )}
      </Cartao>
    </Tela>
  );
}

export const rotaNovaSessao = createRoute({
  getParentRoute: () => rotaPainel,
  path: '/sessoes/nova',
  // Devolve `{}` quando nao ha pasta: assim `<Link to="/sessoes/nova">` sem
  // parametro continua valido, e so quem tem uma pasta em maos precisa passar.
  validateSearch: (busca: Record<string, unknown>): { cwd?: string } =>
    typeof busca['cwd'] === 'string' && busca['cwd'] ? { cwd: busca['cwd'] } : {},
  component: NovaSessao,
});
